import BaseController from "./base_controller"
import type { Substance } from "@/types/models"
import { localizedPath } from "@/utils/i18n"

// Connects to data-controller="substance-dose"
export default class extends BaseController {
  static targets = ["substanceSelect", "doseAmount", "doseUnit", "substanceInfoLink", "substanceInfoName"]
  static values = { 
    substances: Array,
    locale: String
  }

  declare readonly substanceSelectTarget: HTMLSelectElement
  declare readonly doseAmountTarget: HTMLInputElement
  declare readonly doseUnitTarget: HTMLInputElement
  declare readonly substanceInfoLinkTarget: HTMLElement
  declare readonly substanceInfoNameTarget: HTMLElement
  declare readonly hasSubstanceInfoLinkTarget: boolean
  declare readonly hasSubstanceInfoNameTarget: boolean

  declare substancesValue: Substance[]
  declare localeValue: string

  connect(): void {
    super.connect()
    this.debug("SubstanceDose controller connected")
    
    // Substances value is already loaded from data attribute at this point
    // Call update immediately if we have data
    if (this.substancesValue && this.substancesValue.length > 0) {
      this.updateSubstanceInfoLink()
    }
  }
  
  // Called when substancesValue changes after initialization
  substancesValueChanged(): void {
    this.debug(`Substances value changed: ${this.substancesValue.length} substances`)
    // Update link when data changes dynamically
    this.updateSubstanceInfoLink()
  }

  substanceChanged(): void {
    try {
      const selectedId = this.substanceSelectTarget.value
      this.debug(`Substance changed to: ${selectedId}`)
      
      if (!selectedId) {
        this.clearDoseFields()
        this.updateSubstanceInfoLink()
        return
      }

      const substance = this.findSubstanceById(selectedId)
      if (substance && substance.dose_micro && substance.dose_unit) {
        this.fillDoseFields(substance)
        this.debug(`Filled dose: ${substance.dose_micro} ${substance.dose_unit}`)
      } else {
        this.clearDoseFields()
        this.debug("No default dose found for substance - cleared dose fields")
      }
      
      this.updateSubstanceInfoLink()
    } catch (error) {
      this.handleError(error, 'substanceChanged')
    }
  }

  private findSubstanceById(id: string): Substance | undefined {
    return this.substancesValue.find(substance => 
      substance.id.toString() === id
    )
  }

  private fillDoseFields(substance: Substance): void {
    this.doseAmountTarget.value = String(substance.dose_micro)
    this.doseUnitTarget.value = substance.dose_unit
    
    this.doseAmountTarget.dispatchEvent(new Event('change', { bubbles: true }))
    this.doseUnitTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  private clearDoseFields(): void {
    this.doseAmountTarget.value = ""
    this.doseUnitTarget.value = ""
  }

  private updateSubstanceInfoLink(): void {
    try {
      if (!this.hasSubstanceInfoLinkTarget) {
        this.debug("No substance info link target found")
        return
      }

      const selectedId = this.substanceSelectTarget.value
      
      if (!selectedId) {
        this.substanceInfoLinkTarget.style.display = 'none'
        this.debug("Hiding substance info link - no substance selected")
        return
      }

      const substance = this.findSubstanceById(selectedId)
      
      if (substance && substance.slug) {
        const selectedOption = this.substanceSelectTarget.options[this.substanceSelectTarget.selectedIndex]
        const substanceName = selectedOption.text.split(' (')[0]
        
        // Generate correct URL based on locale
        const locale = this.localeValue || 'en'
        const url = `/${locale}/${localizedPath('substances', locale)}/${substance.slug}`
        
        // Update link element
        const linkElement = this.substanceInfoLinkTarget.querySelector('a')
        if (linkElement) {
          linkElement.href = url
          linkElement.target = "_blank"
          linkElement.rel = "noopener noreferrer"
        }
        
        if (this.hasSubstanceInfoNameTarget) {
          this.substanceInfoNameTarget.textContent = substanceName
        }
        
        this.substanceInfoLinkTarget.style.display = 'block'
        this.debug(`Showing substance info link for: ${substanceName} at ${url}`)
      } else {
        this.substanceInfoLinkTarget.style.display = 'none'
        this.debug("Hiding substance info link - substance not found or missing slug")
      }
    } catch (error) {
      this.handleError(error, 'updateSubstanceInfoLink')
    }
  }
}
